import { useMemo, useState } from "react";
import { CheckCircleOutline, ContentPasteGo, HighlightOff } from "@mui/icons-material";
import CheckCircleOutlineOutlined from "@mui/icons-material/CheckCircleOutlineOutlined";
import { Box, Typography } from "@mui/material";
import Accordion from "@components/Accordion/Accordion";
import { Alert } from "@components/Alert/Alert";
import Badge from "@components/Badge/Badge";
import Button from "@components/Button/Button";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import FlowActionButton from "@components/FlowActionButtons/FlowActionButton";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import Subheader from "@components/Subheader/Subheader";
import { SingleDetail } from "@features/vessel-nomination/add/components/header/VesselDetails";
import { useVesselNomination } from "@features/vessel-nomination/add/hooks/useVesselNomination";
import {
  transformNumberToString,
  useVesselNominationFormStore,
} from "@features/vessel-nomination/add/store/useVesselNominationForm.store";
import { usePreNominationRequestFormStore } from "@features/vessel-nomination/pre-nomination-request/store/usePreNominationRequestForm.store";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import { TASK_STATUS } from "@services/tasksApi";
import type { Company } from "@services/vessel-call/types";
import type { VesselNominationDetailsResponse } from "@services/vessel-nomination/types";
import type { Vessel } from "@services/vessel/types";
import { useQueryClient } from "@tanstack/react-query";
import { calculateShipAge, formatDateToDDMMYYYY } from "@utils/date-time";
import { paths } from "@utils/navigation";
import { capitalizeWord } from "@utils/string";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useTask } from "../../hooks/useTask";
import {
  clearNominationUploadFiles,
  hydrateNominationUploadFiles,
  hydratePreNominationUploadFiles,
  mapNominationDetailsToFormData,
  mapPreNominationDetailsToFormData,
} from "../../mappers/nominationDetailsToFormData";
import type { VesselNominationDecision } from "../../types";
import { LANG_BASE, TEST_ID_BASE } from "../../VesselNominationDetails";
import ApprovalDetails from "../side-drawers/ApprovalDetails";
import ApproveRejectSideDrawer from "../side-drawers/ApproveReject";

export const NOMINATION_STATUS = {
  PENDING_APPROVAL: "PENDING_APPROVAL",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  DRAFT: "DRAFT",
};

interface VesselDetailsProps {
  testId: string;
  nominationId: string;
  vesselDetails: Vessel | undefined;
  nominationDetails?: VesselNominationDetailsResponse;
  isFirstRequest?: boolean;
  onDecisionComplete?: (decision: VesselNominationDecision) => void;
}

const VesselDetails: React.FC<VesselDetailsProps> = ({
  testId,
  nominationId,
  vesselDetails,
  nominationDetails,
  isFirstRequest = false,
  onDecisionComplete,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [isOpen, setIsOpen] = useState(true);
  const queryClient = useQueryClient();

  const { nominationTask, decisionNote, vesselCall, vesselCallId, cargoOnboard, status } =
    nominationDetails ?? {};
  const { vcn } = vesselCall ?? {};
  const { vesselCondition } = cargoOnboard ?? {};
  const { conditionDesc } = vesselCondition ?? {};

  const [approvalDrawerType, setApprovalDrawerType] = useState<keyof typeof TASK_STATUS | null>(
    null,
  );
  const [isDetailsDrawerOpen, setIsDetailsDrawerOpen] = useState(false);
  const [isConfirmDiscardDraftOpen, setIsConfirmDiscardDraftOpen] = useState(false);

  const { onDiscard } = useVesselNomination({
    nominationId: nominationDetails?.id,
  });

  const { options: companies } = useFormattedReference<Company>(
    `${REFERENCE_DATA_TYPES.COMPANIES}?isShippingAgency=true`,
    "bff-vessel-call",
  );

  const agencyName = useMemo(() => {
    return companies.find((c) => c.companyCode === user?.companyId)?.companyNameEnglish;
  }, [companies, user]);

  const flag = useMemo(() => {
    if (vesselDetails?.country?.countryCode && vesselDetails?.country?.countryName) {
      return `${vesselDetails.country.countryCode}, ${capitalizeWord(vesselDetails.country.countryName)}`;
    }
    return undefined;
  }, [vesselDetails]);

  const detailsColumns = [
    [
      { label: t(`${LANG_BASE}.details.imo-number`), value: vesselDetails?.vesselImoNumber },
      { label: t(`${LANG_BASE}.details.vessel-name`), value: vesselDetails?.vesselName },
      {
        label: t(`${LANG_BASE}.details.type`),
        value: vesselDetails?.vesselType?.vesselTypeDesc?.toUpperCase(),
      },
    ],
    [
      { label: t(`${LANG_BASE}.details.flag`), value: flag },
      ...(agencyName
        ? [{ label: t(`${LANG_BASE}.details.agency`), value: capitalizeWord(agencyName) }]
        : []),
      {
        label: t(`${LANG_BASE}.details.age`),
        value: calculateShipAge(vesselDetails?.dateOfDelivery, t),
      },
    ],
    [
      { label: t(`${LANG_BASE}.details.loa`), value: vesselDetails?.loa },
      { label: t(`${LANG_BASE}.details.beam`), value: vesselDetails?.beam },
      { label: t(`${LANG_BASE}.details.max-draft`), value: vesselDetails?.draftMax },
    ],
    [
      { label: t(`${LANG_BASE}.details.vessel-condition`), value: conditionDesc },
      { label: t(`${LANG_BASE}.details.gross-tonnage`), value: vesselDetails?.grt },
    ],
  ];

  const vesselCallPath =
    vesselCallId && vcn
      ? `/${paths.vesselCall.index}/${paths.vesselCall.vesselCallPreview}/${vesselCallId}/${paths.vesselCall.vesselDetails}`
      : undefined;
  const nominationReference = nominationDetails?.refOrderNumber ?? nominationId;
  const pageTitle = isFirstRequest
    ? [nominationReference, vesselDetails?.vesselName ?? nominationDetails?.vesselName]
        .filter(Boolean)
        .join(" - ")
    : vesselDetails?.vesselImoNumber;

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["nomination"] });
  };

  const { claimTask, unclaimTask } = useTask({ handleSuccess });

  const canApproveNomination = useMemo(
    () => user?.permissions.approve?.includes("fott-vessel-nomination"),
    [user],
  );

  const canEditNomination = useMemo(
    () => Boolean(user?.permissions.update?.includes("fott-vessel-nomination")),
    [user],
  );

  const isClaimButtonShown = useMemo(() => {
    return !!(isFirstRequest && canApproveNomination && nominationTask?.status === "READY");
  }, [isFirstRequest, nominationTask, canApproveNomination]);

  const isUnclaimButtonShown = useMemo(() => {
    return !!(
      isFirstRequest &&
      canApproveNomination &&
      nominationTask?.status === "CLAIMED" &&
      nominationTask?.claimOwnerName === user?.preferredUsername
    );
  }, [isFirstRequest, nominationTask, canApproveNomination, user]);

  const isApproveRejectButtonShown = useMemo(() => {
    return (
      !!(isFirstRequest && canApproveNomination && nominationTask?.status === "CLAIMED") &&
      nominationTask?.claimOwnerName === user?.preferredUsername
    );
  }, [isFirstRequest, nominationTask, canApproveNomination, user]);

  const hasApprovalNoteOrAttachment = useMemo(() => {
    return (
      [NOMINATION_STATUS.APPROVED, NOMINATION_STATUS.REJECTED].includes(status as string) &&
      (decisionNote?.noteText || decisionNote?.attachmentUrl)
    );
  }, [status, decisionNote]);

  const isResubmitButtonShown = useMemo(() => {
    return canEditNomination && status === NOMINATION_STATUS.REJECTED;
  }, [canEditNomination, status]);

  const isUpdateButtonShown = useMemo(() => {
    return canEditNomination && status === NOMINATION_STATUS.APPROVED;
  }, [canEditNomination, status]);

  const isEditDraftButtonShown = useMemo(() => {
    return canEditNomination && status === NOMINATION_STATUS.DRAFT;
  }, [canEditNomination, status]);

  const isDraftAlertShown = useMemo(() => status === NOMINATION_STATUS.DRAFT, [status]);

  const badgeType = useMemo(() => {
    if (status === NOMINATION_STATUS.APPROVED) return "success";
    if (status === NOMINATION_STATUS.DRAFT) return "neutral";
    if (status === NOMINATION_STATUS.REJECTED) return "error";
    return "warning";
  }, [status]);

  const { setAll, resetForm } = useVesselNominationFormStore();

  const handleUpdate = () => {
    if (!nominationDetails) return;

    resetForm();
    const uploadStore = useUploadStore.getState();

    clearNominationUploadFiles(uploadStore);

    const formData = mapNominationDetailsToFormData(nominationDetails);
    const transformedFormData = transformNumberToString(formData);
    setAll(transformedFormData);
    hydrateNominationUploadFiles(nominationDetails, uploadStore);

    navigate(
      `/${paths.vesselNomination.index}/${paths.vesselNomination.add}/${paths.vesselNomination.preArrivalQuestionnaire}`,
      {
        state: {
          vessel: {
            id: nominationDetails.vesselId,
          },
          vesselNominationDetails: nominationDetails,
        },
      },
    );
  };

  const { setAll: setAllPreNomination, resetForm: resetFormPreNomination } =
    usePreNominationRequestFormStore();

  const handleResubmit = () => {
    if (!nominationDetails) return;

    resetFormPreNomination();
    const uploadStore = useUploadStore.getState();

    clearNominationUploadFiles(uploadStore);
    const formData = mapPreNominationDetailsToFormData(nominationDetails);
    setAllPreNomination(formData);
    hydratePreNominationUploadFiles(nominationDetails, uploadStore);

    navigate(`/${paths.vesselNomination.index}/${paths.vesselNomination.preNominationRequest}`, {
      state: {
        vessel: {
          id: nominationDetails.vesselId,
        },
        nominationId: nominationDetails?.id,
        isResubmitting: true,
      },
    });
  };

  return (
    <>
      <Subheader
        testId={`${testId}-subheader`}
        subtitle={
          <div
            className="flex items-center space-x-2 text-sm text-gray-700"
            data-testid={`${testId}-breadcrumb`}
          >
            <Link
              to="/vessel-nomination"
              className="text-blue-600 hover:underline"
              data-testid={`${testId}-breadcrumb-link`}
            >
              {t("navigation.vessel-nomination")}
            </Link>
            <span className="text-gray-400">/</span>
            <span data-testid={`${testId}-breadcrumb-current`}>
              {isFirstRequest ? nominationReference : vesselDetails?.vesselImoNumber}
            </span>
          </div>
        }
        title={
          <div className="flex items-end justify-between w-full">
            <div>
              <span data-testid={`${testId}-title`}>{pageTitle}</span>
              <div className="flex items-center min-h-10" data-testid={`${testId}-status-badge`}>
                <div className="flex">
                  <Badge
                    type={badgeType}
                    label={status ? t(`${LANG_BASE}.status-labels.${status}`) : t("common.unknown")}
                  />
                </div>
                {hasApprovalNoteOrAttachment && (
                  <div className="flex items-center">
                    <span className="text-base ml-2">–</span>
                    <Button
                      testId={`${TEST_ID_BASE}-view-status-details`}
                      variant="text"
                      type="button"
                      className="ml-4 whitespace-nowrap"
                      onClick={() => setIsDetailsDrawerOpen(true)}
                      label={
                        <span className="normal-case font-medium">
                          {t(`${LANG_BASE}.approval.view-details`)}
                        </span>
                      }
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-4">
              <FlowActionButton
                testId={`${testId}-claim`}
                startIcon={<ContentPasteGo />}
                labelKey={"approval.claim"}
                langBase={LANG_BASE}
                variant="contained"
                isActionAvailable={isClaimButtonShown}
                onClick={() =>
                  claimTask({
                    nominationId: nominationId ?? "",
                    errorTitle: t("vessel-management.notifications.task-claim-error-title"),
                    successTitle: t(`${LANG_BASE}.nomination-claimed`),
                  })
                }
              />
              <FlowActionButton
                testId={`${testId}-unclaim`}
                labelKey={"approval.unclaim"}
                langBase={LANG_BASE}
                variant="outlined"
                isActionAvailable={isUnclaimButtonShown}
                onClick={() =>
                  unclaimTask({
                    nominationId: nominationId ?? "",
                    errorTitle: t("vessel-management.notifications.task-unclaim-error-title"),
                    successTitle: t(`${LANG_BASE}.nomination-unclaimed`),
                  })
                }
              />
              <FlowActionButton
                testId={`${testId}-reject`}
                labelKey={"approval.reject"}
                langBase={LANG_BASE}
                variant="outlined"
                startIcon={<HighlightOff />}
                isActionAvailable={isApproveRejectButtonShown}
                onClick={() => {
                  setApprovalDrawerType(TASK_STATUS.REJECTED as keyof typeof TASK_STATUS);
                }}
              />
              <FlowActionButton
                testId={`${testId}-approve`}
                labelKey={"approval.approve"}
                langBase={LANG_BASE}
                variant="contained"
                startIcon={<CheckCircleOutline />}
                isActionAvailable={isApproveRejectButtonShown}
                onClick={() => {
                  setApprovalDrawerType(TASK_STATUS.APPROVED as keyof typeof TASK_STATUS);
                }}
              />
              <FlowActionButton
                testId={`${testId}-resubmit`}
                labelKey={"resubmit"}
                langBase={LANG_BASE}
                variant="contained"
                isActionAvailable={isResubmitButtonShown}
                onClick={handleResubmit}
              />
              <FlowActionButton
                testId={`${testId}-update`}
                labelKey={"update"}
                langBase={LANG_BASE}
                startIcon={<CheckCircleOutlineOutlined />}
                variant="contained"
                isActionAvailable={isUpdateButtonShown}
                onClick={handleUpdate}
              />
              <FlowActionButton
                testId={`${testId}-discard-draft`}
                labelKey={"discard"}
                langBase={"common"}
                variant="text"
                onClick={() => setIsConfirmDiscardDraftOpen(true)}
                isActionAvailable={isEditDraftButtonShown}
              />
              <FlowActionButton
                testId={`${testId}-edit-draft`}
                labelKey={"edit"}
                langBase={"common"}
                variant="contained"
                onClick={handleUpdate}
                isActionAvailable={isEditDraftButtonShown}
              />
            </div>
          </div>
        }
      />
      {isDraftAlertShown && (
        <Alert type="info">
          {t(`common.draft-info`, {
            date: nominationDetails?.draftExpirationDate
              ? formatDateToDDMMYYYY(nominationDetails?.draftExpirationDate)
              : "-",
          })}
        </Alert>
      )}
      <Box sx={{ px: 12, pt: 4 }}>
        <Accordion
          testId={`${testId}-accordion`}
          summary={<Typography variant="body2">{t(`${LANG_BASE}.details.title`)}</Typography>}
          expanded={isOpen}
          onChange={() => setIsOpen(!isOpen)}
          isTransparent={true}
        >
          <div className="grid grid-cols-1 gap-y-6 md:grid-cols-5 md:gap-y-0 mt-2">
            {!isFirstRequest && (
              <div className="min-w-0 md:col-span-1 md:border-r md:border-slate-200 md:pr-8">
                {vesselCallPath ? (
                  <div className="flex flex-col min-w-0">
                    <dt className="text-sm text-slate-700">
                      {t(`${LANG_BASE}.details.vessel-call`)}
                    </dt>
                    <dd className="mt-0.5 min-w-0 text-sm font-bold">
                      <Link
                        to={vesselCallPath}
                        className="block w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-blue-600 hover:underline"
                        data-testid={`${testId}-vessel-call-link`}
                      >
                        {nominationDetails?.vesselCall?.vcn}
                      </Link>
                    </dd>
                  </div>
                ) : (
                  <SingleDetail label={t(`${LANG_BASE}.details.vessel-call`)} value="-" />
                )}
              </div>
            )}
            <div
              className={`grid min-w-0 gap-y-6 md:grid-cols-2 md:gap-x-8 xl:grid-cols-4 ${
                isFirstRequest ? "md:col-span-5" : "md:col-span-4 md:pl-8"
              }`}
            >
              {detailsColumns.map((col, idx) => (
                <div key={idx} className="min-w-0">
                  <div className="space-y-6">
                    {col.map((item) => (
                      <SingleDetail key={item.label} label={item.label} value={item.value} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Accordion>
      </Box>
      {approvalDrawerType && (
        <ApproveRejectSideDrawer
          nominationId={nominationId}
          isOpen
          onClose={() => setApprovalDrawerType(null)}
          type={approvalDrawerType}
          onDecisionComplete={onDecisionComplete}
        />
      )}
      <ApprovalDetails
        isOpen={isDetailsDrawerOpen}
        onClose={() => setIsDetailsDrawerOpen(false)}
        noteText={decisionNote?.noteText}
        attachmentUrl={decisionNote?.attachmentUrl}
        attachmentName={decisionNote?.documentName}
      />
      <GenericDialog
        open={isConfirmDiscardDraftOpen}
        title={t(`${LANG_BASE}.approval.discard-title`)}
        content={t(`${LANG_BASE}.approval.discard-description`)}
        cancelButtonText={t("common.cancel")}
        confirmButtonText={t("common.discard")}
        handleCloseDialog={() => setIsConfirmDiscardDraftOpen(false)}
        confirmAction={onDiscard}
        testId="discard-draft-confirm-dialog"
        maxWidth="sm"
      />
    </>
  );
};

export default VesselDetails;
